import { useState } from 'react';
import { CgArrowLeft } from 'react-icons/cg';
import { Alert } from '@nextui-org/react';
import { Link } from 'react-router';

import { Button } from '@/components/Button';
import { CallDuration } from '@/components/CallDuration';
import { GoalIcon } from '@/components/icons/GoalIcon';
import { FeedbackModal } from './feedback-modal';
import audio from '/icons/audio-white.svg';
import audio_speak from '/icons/audio_speak.gif';
import logo from '/icons/octopus_logo.png';
import { useSimulation } from '@/hooks/useSimulation';
import { convertToHHMMSS, convertToSeconds } from '@/helper/formatTime';
import { DonorScenario } from '@/types/scenarios';

// Mock user data for demo
const mockUser = {
  id: 'jordan-user',
  email: 'jordan@demo.fundraising.com',
  firstName: 'Jordan'
};

const defaultBalance = '00:05:00';

interface SimulationProps extends DonorScenario {
  instructions: string;
  openingSentence: string;
  donorScenarioPromptId: string;
}

const alertMessages = {
  insufficientBalance: "Oh no! It doesn't look like you have enough talk time to start a simulation!",
  balanceLimitReached: "Oh no! You've used up all your talk time!"
};

export const Simulation = ({ scenario }: { scenario: SimulationProps }) => {
  const [isOpenFeedback, setIsOpenFeedback] = useState(false);

  const userBalance = localStorage.getItem('simulation-limit') ?? defaultBalance;
  const currentLimitInSeconds = convertToSeconds(userBalance);

  const simulationConfig = {
    user: mockUser,
    balance: userBalance,
    isFirstSimulation: true,
    scenario: {
      simulationTitle: scenario.title,
      practiceType: 'Scenario',
      gender: scenario.gender,
      ageRange: '41-56',
      instructions: scenario.instructions.replace('{{session_level}}', 'Easy'),
      openingSentence: scenario.openingSentence,
      goals: [scenario.goal],
      config: {
        donorScenarioId: scenario.id,
        donorScenarioPromptId: scenario.donorScenarioPromptId,
        level: 'Easy'
      }
    }
  };

  const handleChangePlaying = () => {
    simulation.isPlaying ? simulation.endSimulation() : simulation.startSimulation();
  };

  const handleCloseFeedback = () => {
    setIsOpenFeedback(false);
  };

  const handleUpdateLimit = (duration: number) => {
    const newLimitInSeconds = Math.max(0, currentLimitInSeconds - duration);
    const newLimitFormatted = convertToHHMMSS(newLimitInSeconds);

    localStorage.setItem('simulation-limit', newLimitFormatted);
  };

  const simulation = useSimulation(simulationConfig, {
    onStart: () => console.log('started'),
    onEnd: () => {
      setIsOpenFeedback(true);
      console.log('ended');
    },
    onBalanceDecrease: seconds => handleUpdateLimit(seconds)
  });

  const alertMessage =
    simulation.status === 'balanceLimitReached'
      ? alertMessages.balanceLimitReached
      : currentLimitInSeconds < 60
        ? alertMessages.insufficientBalance
        : null;

  return (
    <>
      {/* Simulation */}
      <div
        data-testid="simulation"
        className="flex-1 relative right-side text-white py-10 px-6 flex flex-col items-center justify-center gap-5 min-h-[60svh]"
      >
        <div className="absolute top-5 left-5 flex items-center gap-3">
          <Button
            mini
            to="/demo"
            component={Link}
            variant="outline"
            disabled={simulation.isPlaying}
            icon={<CgArrowLeft size={18} />}
            className="text-white border-white/50 hover:bg-white hover:text-black disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-white"
          />
          <h2 className="hidden md:block text-xl font-semibold">Scenario: {scenario.title}</h2>
        </div>
        <div className="absolute right-5 top-5 space-y-2 max-w-[250px]">
          <CallDuration
            callLimit={userBalance}
            isPlaying={simulation.isPlaying}
            formattedDuration={simulation.formattedDuration}
          />
          <div className="hidden md:block bg-[#66727e4d] flex-col gap-1 p-4 rounded-lg text-sm text-white font-light w-full">
            <div className="flex items-center gap-2">
              <GoalIcon size={18} fill="#FE6840" /> <span className="font-bold">Goal</span>
            </div>
            {scenario.goal}
          </div>
        </div>

        <div className="rounded-full donor-chat-bg flex items-center justify-center w-64 h-64">
          <img src={logo} alt="Octopus Logo" className="w-auto !h-20" />
        </div>

        <img alt="Audio" width={230} height={30} src={simulation.assistantIsSpeaking ? audio_speak : audio} />

        <Button
          onClick={handleChangePlaying}
          disabled={simulation.isLoading || (!simulation.isPlaying && currentLimitInSeconds < 60)}
          label={simulation.isPlaying ? 'Finish' : simulation.playButtonLabel}
        />

        {alertMessage && (
          <span className="mt-5">
            <Alert color="danger" title={alertMessage} />
          </span>
        )}
      </div>

      {/* Post-Simulation */}
      {isOpenFeedback && scenario && <FeedbackModal scenario={scenario} onOpenChange={handleCloseFeedback} />}
    </>
  );
};
